import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { GetHistory } from 'app/components/router/History';
import {
  parseAuthenticateRequest,
  IAuthenticateRequest,
} from 'app/services/oauth/AuthenticateRequest';
import { delegate } from 'app/services/oauth/delegate';
import { getAuthUser } from 'app/models/auth/selectors';
import { User } from 'app/constants/schemas';
import { AppDispatch } from 'redux';
import { History } from 'app/services/history';
import { AppState } from 'app/store';
import { LoginRoute } from 'app/constants/Routes';

type IProvidedProps = IState;

interface IProps {
  dispatch: AppDispatch;
  requestParams: { [index: string]: unknown };
  history: History;
  authUser: User;

  children: (state: IProvidedProps) => ReactNode;
}

interface IState {
  request: IAuthenticateRequest | null;
  loading: boolean;
  error?: Error;
}

class OauthDelegate extends React.Component<IProps, IState> {
  private mounted: boolean = false;

  public constructor(initialProps: IProps) {
    super(initialProps);

    let request   = null;
    let error;
    const loading = false;

    try {
      request = parseAuthenticateRequest(initialProps.requestParams);
    } catch (e) {
      error = e;
    }

    this.state = { request, loading, error };
  }

  // If we use `componentDidMount`, then mounted is false when we call
  // onDelegate.  `onDelegate` is called from the Component's
  // `componentDidMount` method, which is why the timing is delicate.
  public componentDidMount() {
    this.mounted = true;

    this.delegateIfProfileComplete();
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public render() {
    return this.props.children(this.state);
  }

  private async onDelegate() {
    const { dispatch, history } = this.props;
    const { request }           = this.state;

    this.setState({ loading: true });

    try {
      if (!request) {
        history.replace(LoginRoute.getPathname());
        return;
      }

      if (request.response_type !== 'code') {
        throw new Error(
          'Only authorization code (\'code\')  requests are allowed, ' +
          `but '${request.response_type}' was specified`);
      }

      const delegateResult = await dispatch(delegate(request));

      if (!this.mounted) {
        return;
      }

      history.hardReplace(delegateResult.redirectTo);
    } catch (e) {
      if (this.mounted) {
        this.setState({ error: e });
      }
    } finally {
      if (this.mounted) {
        this.setState({ loading: false });
      }
    }
  }

  private delegateIfProfileComplete = () => {
    // Redirect middleware will send the user to the profile incomplete page
    const { authUser } = this.props;
    if (authUser && authUser.profileCompleted) {
      this.onDelegate();
    }
  };
}

const mapStateToProps = (state: AppState) => ({
  authUser: getAuthUser(state),
});

const OauthDelegateConnected = connect(mapStateToProps, (dispatch) => ({ dispatch }))(OauthDelegate);

const OauthDelegateInjectDependencies = (
  props: Omit<IProps, 'history' | 'authUser' | 'dispatch'>,
) => (
  <GetHistory>{(history) =>
    <OauthDelegateConnected
      {...props}
      history={history}
    />
  }</GetHistory>
);

export const OauthDelegateUnconnected = OauthDelegate;
export default OauthDelegateInjectDependencies;
